"use client";

import { ArrowUpRight, Calendar, Mail, Phone } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border rounded-lg bg-background/60">
        <p className="font-default text-2xl text-foreground mb-4">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.18em] uppercase font-medium text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Alex Rivera" {...field} className="border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:border-primary focus-visible:ring-0 placeholder:text-muted-foreground/50 text-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.18em] uppercase font-medium text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="alex@codehub.dev" {...field} className="border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:border-primary focus-visible:ring-0 placeholder:text-muted-foreground/50 text-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.18em] uppercase font-medium text-muted-foreground">
              Discord Handle
            </FormLabel>
            <FormControl>
              <Input type="text" placeholder="alex_dev#1234" {...field} className="border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:border-primary focus-visible:ring-0 placeholder:text-muted-foreground/50 text-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="tech_stack" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.18em] uppercase font-medium text-muted-foreground">
              Primary Tech Stack
            </FormLabel>
            <FormControl>
              <Input placeholder="React, Rust, Python" {...field} className="border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:border-primary focus-visible:ring-0 placeholder:text-muted-foreground/50 text-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="interest_area" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.18em] uppercase font-medium text-muted-foreground">
              Area of Interest
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="w-full border-0 border-b border-border bg-transparent px-0 h-10 focus-visible:border-primary focus-visible:ring-0 text-foreground data-[placeholder]:text-muted-foreground/50">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover">
                <SelectItem value="hackathons">Hackathons</SelectItem>
                <SelectItem value="workshops">Workshops</SelectItem>
                <SelectItem value="projects">Project Building</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.18em] uppercase font-medium text-muted-foreground">
              Tell us about your goals
            </FormLabel>
            <FormControl>
              <Textarea placeholder="What are you building?" rows={4} {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:border-primary focus-visible:ring-0 placeholder:text-muted-foreground/50 text-foreground resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/30 rounded-md bg-destructive/5">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-300 shadow-md hover:shadow-lg group">
          <span className="tracking-wide">
            {isSubmitting ? "Sending request..." : "Join the Hub"}
          </span>
          <ArrowUpRight className="size-4 ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Button>
        <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
          By submitting, you agree to join our community discord and receive updates on upcoming hackathons and workshops.
        </p>
      </div>
    </Form>;
}
export default function GetConnected() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const contactDetails = [{
    icon: Calendar,
    label: "Weekly Meetups",
    value: "Every Thursday, 7pm EST"
  }, {
    icon: Phone,
    label: "Community Hub",
    value: "Discord Server"
  }, {
    icon: Mail,
    label: "Support Email",
    value: "hello@codehub.dev"
  }];
  return <section data-section-id="4597" id="get-connected" className="bg-background text-foreground pt-24 md:pt-32 lg:pt-36 pb-24 md:pb-28 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="text-[11px] tracking-[0.24em] uppercase font-medium text-primary mb-8">
              Get Connected
            </p>

            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.05] tracking-tight text-foreground mb-8">
              Build the{" "}
              <span className="italic font-light text-primary">future</span>
              .
            </h2>

            <p className="text-lg leading-relaxed text-foreground/80 mb-12 max-w-md font-default">
              Join a community of builders, hackers, and creators. Whether you're debugging your first project or deploying a production app, Code Hub is your home base.
            </p>

            <div className="space-y-0 mb-10">
              {contactDetails.map((item, i) => {
              const Icon = item.icon;
              return <div key={i} data-index={i} className="group flex items-start gap-5 py-6 border-t border-border last:border-b transition-colors hover:bg-accent/30">
                    <Icon className="size-4 text-primary mt-1 shrink-0" strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-[10px] tracking-[0.22em] uppercase font-medium text-muted-foreground mb-2">
                        {item.label}
                      </p>
                      <p className="text-base text-foreground group-hover:text-primary transition-colors">
                        <span className="border-b border-transparent group-hover:border-primary/60 transition-colors">
                          {item.value}
                        </span>
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <p className="italic text-primary text-sm font-default">
              Open source, community driven.
            </p>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="bg-card rounded-lg p-8 md:p-12 lg:p-14 shadow-md">
              <div className="mb-10 pb-8 border-b border-border">
                <p className="text-[11px] tracking-[0.24em] uppercase font-medium text-primary mb-3">
                  Membership Form
                </p>
                <h3 className="font-default text-3xl md:text-4xl text-foreground leading-tight">
                  Apply for membership
                </h3>
              </div>

              <FormProvider formKey="connect_form_join_us" formId="connect_form_join_us" sectionName="get-connected" defaultValues={{
              name: "",
              email: "",
              phone: "",
              tech_stack: "",
              interest_area: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: false
              },
              tech_stack: {
                required: false
              },
              interest_area: {
                required: false
              },
              message: {
                required: true
              }
            }} submitText="Join the Hub" successMessage="Welcome to the community! Check your email for Discord invite details." errorMessage="Connection failed. Please try again.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}